import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Markdown → React elements.
 *
 * Security: markdown-it runs with `html: false` (raw HTML is escaped as text), and the token
 * stream is rendered straight to React elements — the viewer never uses innerHTML, so agent
 * output cannot inject markup. Links pass an allowlist (`safeUrl`); remote images are not
 * auto-loaded (rendered as links) to avoid tracking pixels.
 *
 * Headings are shifted down (h1→h4 …) so the transcript outline stays: session → turn (h3) →
 * message headings.
 */
import MarkdownIt from 'markdown-it';
import { createElement, Fragment, memo, useMemo } from 'react';
import { CodeBlock } from '../components/CodeBlock.js';
import { mayContainRefs, RefLink, RefText } from '../components/RefLink.js';
import { refFromCode, refFromHref } from './refs.js';
const md = new MarkdownIt({ html: false, linkify: true, typographer: false, breaks: false });
md.validateLink = (url) => safeUrl(url) != null;
// `README.md`, `setup.sh` are files, not Moldovan/Saint Helena domains (openable-refs).
md.linkify.set({ fuzzyLink: false });
export function safeUrl(url) {
    const u = url.trim();
    if (/^(https?:|mailto:)/i.test(u))
        return u;
    if (/^[a-z][a-z0-9+.-]*:/i.test(u))
        return null; // javascript:, data:, vbscript:, file:, …
    if (u.startsWith('//'))
        return null;
    return u;
}
const HEADING_SHIFT = {
    h1: 'h4',
    h2: 'h5',
    h3: 'h6',
    h4: 'h6',
    h5: 'h6',
    h6: 'h6',
};
function attrsToProps(t) {
    const props = {};
    for (const [k, v] of t.attrs ?? []) {
        if (k === 'style') {
            const m = v.match(/text-align:\s*(left|right|center)/);
            if (m)
                props.style = { textAlign: m[1] };
        }
        else if (k === 'start')
            props.start = Number(v);
    }
    return props;
}
function renderInline(tokens, keyBase) {
    const root = { tag: 'root', props: {}, children: [] };
    const stack = [root];
    const top = () => stack[stack.length - 1];
    tokens.forEach((t, i) => {
        const key = `${keyBase}-${i}`;
        switch (t.type) {
            case 'text':
                // Inside a ref link the link itself is the reference.
                top().children.push(!top().ref && mayContainRefs(t.content) ? (_jsx(RefText, { text: t.content }, key)) : (t.content));
                break;
            case 'code_inline': {
                const code = (_jsx("code", { className: "vk-md-code", children: t.content }, key));
                const ref = top().ref ? null : refFromCode(t.content);
                top().children.push(ref ? (_jsx(RefLink, { refValue: ref, className: "vk-ref--code", children: code }, key)) : (code));
                break;
            }
            case 'softbreak':
                top().children.push('\n');
                break;
            case 'hardbreak':
                top().children.push(_jsx("br", {}, key));
                break;
            case 'image': {
                const src = safeUrl(t.attrGet('src') ?? '');
                const alt = t.content || 'image';
                top().children.push(src ? (_jsxs("a", { href: src, target: "_blank", rel: "noopener noreferrer", className: "vk-md-link", children: ["[image: ", alt, "]"] }, key)) : (`[image: ${alt}]`));
                break;
            }
            case 'link_open': {
                const raw = t.attrGet('href') ?? '';
                const ref = refFromHref(raw);
                if (ref) {
                    stack.push({ tag: 'a', props: {}, children: [], ref });
                    break;
                }
                const href = safeUrl(raw);
                stack.push({
                    tag: 'a',
                    props: href
                        ? { href, target: '_blank', rel: 'noopener noreferrer', className: 'vk-md-link' }
                        : { className: 'vk-md-link' },
                    children: [],
                });
                break;
            }
            case 'strong_open':
            case 'em_open':
            case 's_open': {
                stack.push({ tag: t.tag, props: {}, children: [] });
                break;
            }
            case 'link_close': {
                const f = stack.pop();
                if (f.ref) {
                    // No resolver: a local URL stays a normal link; a path never becomes a dead app link.
                    const href = f.ref.kind === 'url' ? safeUrl(f.ref.text) : null;
                    top().children.push(_jsx(RefLink, { refValue: f.ref, fallback: href ? (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer", className: "vk-md-link", children: f.children })) : (_jsx("span", { className: "vk-md-pathlink", children: f.children })), children: f.children }, key));
                }
                else
                    top().children.push(createElement(f.tag, { key, ...f.props }, ...f.children));
                break;
            }
            case 'strong_close':
            case 'em_close':
            case 's_close': {
                const f = stack.pop();
                top().children.push(createElement(f.tag, { key, ...f.props }, ...f.children));
                break;
            }
            case 'html_inline':
                top().children.push(t.content);
                break;
            default:
                if (t.content)
                    top().children.push(t.content);
        }
    });
    return root.children;
}
function renderBlocks(tokens) {
    const root = { tag: 'root', props: {}, children: [] };
    const stack = [root];
    const top = () => stack[stack.length - 1];
    tokens.forEach((t, i) => {
        const key = `b${i}`;
        if (t.nesting === 1) {
            let tag = t.tag;
            if (/^h[1-6]$/.test(tag))
                tag = HEADING_SHIFT[tag] ?? 'h6';
            const props = attrsToProps(t);
            if (/^h[1-6]$/.test(t.tag))
                props.className = `vk-md-h vk-md-${t.tag}`;
            if (tag === 'table') {
                stack.push({ tag: 'div', props: { className: 'vk-md-table-wrap' }, children: [] });
            }
            // Tight list items wrap their text in hidden paragraphs; markdown-it marks them hidden.
            stack.push({ tag, props, children: [] });
            return;
        }
        if (t.nesting === -1) {
            const f = stack.pop();
            const el = createElement(f.tag, { key, ...f.props }, ...f.children);
            top().children.push(el);
            if (t.tag === 'table') {
                const wrap = stack.pop();
                top().children.push(createElement(wrap.tag, { key: `${key}w`, ...wrap.props }, ...wrap.children));
            }
            return;
        }
        switch (t.type) {
            case 'inline':
                top().children.push(_jsx(Fragment, { children: renderInline(t.children ?? [], key) }, key));
                break;
            case 'fence':
            case 'code_block': {
                const lang = t.info.trim().split(/\s+/)[0] || undefined;
                top().children.push(_jsx(CodeBlock, { code: t.content.replace(/\n$/, ''), lang: lang }, key));
                break;
            }
            case 'hr':
                top().children.push(_jsx("hr", {}, key));
                break;
            default:
                if (t.content)
                    top().children.push(_jsx("p", { children: t.content }, key));
        }
    });
    // Unwrap hidden paragraphs of tight lists.
    return root.children;
}
function renderMarkdown(src) {
    const tokens = md.parse(src, {});
    // Tight lists: markdown-it marks paragraph tokens hidden — drop their open/close.
    const visible = tokens.filter((t) => !(t.hidden && (t.type === 'paragraph_open' || t.type === 'paragraph_close')));
    return renderBlocks(visible);
}
/** Split at blank lines outside code fences so streaming only re-parses the last block. */
export function splitBlocks(src) {
    const out = [];
    let buf = [];
    let fence = null;
    for (const line of src.split('\n')) {
        const m = line.match(/^\s*(```+|~~~+)/);
        if (m?.[1]) {
            if (!fence)
                fence = m[1];
            else if (m[1].startsWith(fence[0]) && m[1].length >= fence.length)
                fence = null;
        }
        if (!fence && line.trim() === '' && buf.length) {
            out.push(buf.join('\n'));
            buf = [];
            continue;
        }
        buf.push(line);
    }
    if (buf.length)
        out.push(buf.join('\n'));
    return out;
}
const Block = memo(function Block({ src }) {
    return _jsx(_Fragment, { children: renderMarkdown(src) });
});
export function Markdown({ text, streaming = false, className, }) {
    const blocks = useMemo(() => (streaming ? splitBlocks(text) : null), [text, streaming]);
    const whole = useMemo(() => (streaming ? null : renderMarkdown(text)), [text, streaming]);
    return (_jsx("div", { className: `vk-md${streaming ? ' vk-md--streaming' : ''}${className ? ` ${className}` : ''}`, children: blocks
            ? // biome-ignore lint/suspicious/noArrayIndexKey: blocks are positional; memo skips unchanged ones
                blocks.map((b, i) => _jsx(Block, { src: b }, i))
            : whole }));
}
//# sourceMappingURL=markdown.js.map